// Knowledge Base document detail (Session AFA). Shows the document's file facts and processing
// status, its AI summary, its tags (editable inline), the extracted text (collapsible), and the
// delete control (the two-stage soft-then-hard flow). A FAILED document offers a Retry. Office/admin.
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import {
  DocumentDeleteModals,
  TagFields,
  buildTagPatch,
  labelCls,
  seedTagForm,
  type TagFormState,
} from "./knowledgeUi";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import {
  STORAGE_SOURCE_LABEL,
  docTypeLabel,
  docTypePill,
  formatFileSize,
  knowledgeShortDate,
  type KnowledgeDocumentDetail,
} from "../../lib/knowledgeTypes";

export function KnowledgeDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<KnowledgeDocumentDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setState("loading");
      try {
        const res = await api.knowledge.documents.get(id);
        setDoc(res.document);
        setState("ready");
      } catch (err) {
        setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    },
    [id],
  );

  useEffect(() => {
    void load();
  }, [load]);
  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That document could not be found.</p>;
  if (state === "error" || !doc) return <p role="alert" className="p-6 text-center text-ele-strong">Could not load this document. Please try again.</p>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <RecordHeader
        className="mb-4"
        title={<span className="min-w-0 break-all text-2xl sm:text-3xl">{doc.fileName}</span>}
        backTo="/office/knowledge"
        backLabel="Back to the Knowledge Base"
        status={
          <>
            <StatusPill domain="knowledgeIngest" status={doc.ingestStatus} />
            <StatusPill word={docTypeLabel(doc.docType)} tone={docTypePill()} />
          </>
        }
      />

      {doc.ingestStatus === "FAILED" && <FailedPanel doc={doc} onRetried={() => load({ silent: true })} />}

      <FileFactsSection doc={doc} />

      {doc.aiTagsSummary && (
        <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
          <h2 className="mb-2 font-heading text-lg text-ele-blue">AI summary</h2>
          <p className="whitespace-pre-wrap text-sm text-ele-body">{doc.aiTagsSummary}</p>
        </section>
      )}

      <TagsSection doc={doc} onChanged={() => load({ silent: true })} />

      <ExtractedTextSection text={doc.extractedText} />

      <section className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-5">
        <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
          Delete document
        </Button>
      </section>

      <DocumentDeleteModals
        doc={deleteOpen ? doc : null}
        onClose={() => {
          setDeleteOpen(false);
          // A soft delete may have hidden it server-side; reload so the page reflects that.
          void load();
        }}
        onDeleted={() => navigate("/office/knowledge")}
      />
    </div>
  );
}

function FailedPanel({ doc, onRetried }: { doc: KnowledgeDocumentDetail; onRetried: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function retry() {
    setBusy(true);
    setError(null);
    try {
      await api.knowledge.documents.retry(doc.id);
      onRetried();
    } catch (err) {
      setError(friendlyApiError(err, "Could not re-queue this document. Please try again."));
      setBusy(false);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-danger bg-ele-surface p-5">
      <h2 className="mb-1 font-heading text-lg text-ele-danger">Processing failed</h2>
      <p className="text-sm text-ele-body">{doc.ingestError ?? "This document could not be processed."}</p>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      <button
        onClick={retry}
        disabled={busy}
        className="mt-3 rounded-ele border border-ele-blue px-4 py-2 font-menu text-xs tracking-wide text-ele-blue hover:bg-ele-blue/10 disabled:opacity-50"
      >
        {busy ? "Re-queuing..." : "Retry"}
      </button>
    </section>
  );
}

function FileFactsSection({ doc }: { doc: KnowledgeDocumentDetail }) {
  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Document</h2>
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        <Fact label="File size" value={formatFileSize(doc.fileSize)} />
        <Fact label="File type" value={doc.mimeType || "Unknown"} />
        <Fact label="Source" value={STORAGE_SOURCE_LABEL[doc.storageSource]} />
        <Fact label="Pages" value={<PagesFactValue doc={doc} />} />
        <Fact label="Searchable sections" value={String(doc.chunkCount)} />
        <Fact label="Added" value={knowledgeShortDate(doc.createdAt)} />
      </dl>
    </section>
  );
}

// The page count, and how much of it was actually read. The extractor stops at a fixed cap, so a long
// manufacturer manual can show "Pages: 120" beside a green Ready pill while only the first stretch of
// it was ever extracted, chunked and embedded. Without this line, an answer of "that is not in the
// knowledge base" can really mean "that is on page 18". Stated plainly and quietly, in the ordinary
// body colour: it is a fact about the document, not a warning.
function PagesFactValue({ doc }: { doc: KnowledgeDocumentDetail }) {
  if (doc.pageCount == null) return <>Not known</>;
  const indexed = doc.pagesIndexed;
  const partial = indexed != null && indexed < doc.pageCount;
  return (
    <>
      {doc.pageCount}
      {partial && (
        <span className="mt-1 block text-xs text-ele-body">
          Only the first {indexed} {indexed === 1 ? "page was" : "pages were"} read and searched.
        </span>
      )}
    </>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className={labelCls}>{label}</dt>
      <dd className="break-words text-sm text-ele-strong">{value}</dd>
    </div>
  );
}

function TagsSection({
  doc,
  onChanged,
}: {
  doc: KnowledgeDocumentDetail;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<TagFormState>(() => seedTagForm(doc));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    // Re-seed the edit form from the latest document whenever it reloads.
    setForm(seedTagForm(doc));
  }, [doc]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.knowledge.documents.patchTags(doc.id, buildTagPatch(form));
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(friendlyApiError(err, "Could not save the tags. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function confirmTags() {
    setConfirming(true);
    setError(null);
    try {
      await api.knowledge.documents.patchTags(doc.id, { aiTagsConfirmed: true });
      onChanged();
    } catch (err) {
      setError(friendlyApiError(err, "Could not confirm the tags. Please try again."));
      setConfirming(false);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg text-ele-blue">Tags</h2>
        {!editing && (
          <button
            onClick={() => {
              setForm(seedTagForm(doc));
              setError(null);
              setEditing(true);
            }}
            className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
          >
            Edit
          </button>
        )}
      </div>

      {!doc.aiTagsConfirmed && !editing && (
        <div className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 p-4">
          <p className="text-sm text-amber-300">These tags were suggested by AI and have not been confirmed yet. Check them and confirm.</p>
          {error && !editing && (
            <p role="alert" className="mt-2 text-sm text-ele-danger">
              {error}
            </p>
          )}
          <button
            onClick={confirmTags}
            disabled={confirming}
            className="mt-3 rounded-ele bg-amber-500 px-4 py-2 font-heading text-sm tracking-wide text-ele-bg disabled:opacity-50"
          >
            {confirming ? "Confirming..." : "Confirm tags"}
          </button>
        </div>
      )}

      {editing ? (
        <form onSubmit={save}>
          <div className="grid gap-3 sm:grid-cols-2">
            <TagFields form={form} onChange={setForm} idPrefix="detail-tag" />
          </div>
          {error && (
            <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {error}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            <Button type="submit" disabled={busy}>
              {busy ? "Saving..." : "Save tags"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
              className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Fact label="Site" value={doc.siteName ?? "Not linked"} />
          <Fact label="Lift" value={doc.eleNumber ?? "Not linked"} />
          <Fact label="Manufacturer" value={doc.manufacturer ?? "Not set"} />
          <Fact label="Model" value={doc.liftModel ?? "Not set"} />
          <Fact label="Document type" value={docTypeLabel(doc.docType)} />
          <div className="sm:col-span-2">
            <dt className={labelCls}>Fault categories</dt>
            <dd className="mt-1">
              {doc.faultCategories.length === 0 ? (
                <span className="text-sm text-ele-body opacity-70">None</span>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {doc.faultCategories.map((c) => (
                    <StatusPill key={c} word={c} tone="purple" />
                  ))}
                </div>
              )}
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
}

function ExtractedTextSection({ text }: { text: string | null }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={open}
      >
        <span className="font-heading text-lg text-ele-blue">Extracted text</span>
        <span className="font-menu text-xs uppercase tracking-wide text-ele-body">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-3">
          {text && text.trim() ? (
            <pre data-vscroll className="max-h-96 overflow-auto whitespace-pre-wrap rounded-ele border border-ele-hairline bg-ele-inset p-3 text-xs text-ele-body">{text}</pre>
          ) : (
            <p className="text-sm text-ele-body opacity-70">No text has been extracted from this document yet.</p>
          )}
        </div>
      )}
    </section>
  );
}
